import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../../services/api/onboarding';
import { toast } from 'react-hot-toast';
import SidePanel from '../common/SidePanel';
import NewMarkets from './NewMarkets';
import ProductInformation from './ProductInformation';
import DemandForecast from './DemandForecast';
import UploadDocuments from './UploadDocuments';
import LoadingSpinner from '../common/LoadingSpinner';
import { Steps } from 'antd';

function OnboardingLayout() {
  const [currentStep, setCurrentStep] = useState('markets');
  const [steps, setSteps] = useState({
    markets: false,
    forecast: false
  });
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const stepInfo = [
    { id: 'markets', title: 'Markets', description: 'Select your current and target markets' },
    { id: 'forecast', title: 'Demand Forecast', description: 'Estimate market demand' }
  ];

  useEffect(() => {
    const fetchCurrentStep = async () => {
      try {
        const data = await onboardingApi.getCurrentStep();
        console.log('Onboarding data:', data);
        setCurrentStep(data.currentStep);
        setSteps(data.steps);
        setProgress(data.progress);
      } catch (error) {
        console.error('Failed to fetch current step:', error);
        toast.error('Failed to load onboarding progress');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentStep();
  }, []);

  const stepComponents = {
    markets: NewMarkets,
    forecast: DemandForecast
  };

  const handleNext = () => {
    const stepOrder = ['markets', 'forecast'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      setCurrentStep(nextStep);
      navigate(`/onboarding/${nextStep}`);
    }
  };

  const handlePrevious = () => {
    const stepOrder = ['markets', 'forecast'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      setCurrentStep(prevStep);
      navigate(`/onboarding/${prevStep}`);
    }
  };

  const getCurrentStepIndex = () => {
    const stepOrder = ['markets', 'forecast'];
    return stepOrder.indexOf(currentStep);
  };

  if (loading) {
    return <LoadingSpinner message="Loading your progress..." />;
  }

  const CurrentStepComponent = stepComponents[currentStep] || (() => <p>Step not found</p>);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar - Hide on mobile */}
        <div className="hidden md:block md:fixed md:left-0 md:w-[400px]">
          <SidePanel 
            title="Ready to expand?"
            subtitle="Answer these questions to easily expand your market to the Middle East!"
            steps={stepInfo}
            currentStep={currentStep}
            completedSteps={Object.entries(steps)
              .filter(([_, completed]) => completed)
              .map(([step]) => step)}
          />
        </div>

        {/* Main Content - Adjust margin for mobile */}
        <div className="flex-1 md:ml-[400px]">
          {/* Floating Header Section with Steps */}
          <header className="sticky top-4 w-full flex justify-center z-30">
            <div className="bg-white rounded-xl shadow-lg px-8 py-4">
              <Steps
                current={getCurrentStepIndex()}
                items={[
                  { title: 'Markets' },
                  { title: 'Forecast' }
                ]}
              />
            </div>
          </header>

          {/* Content Section */}
          <main className="max-w-7xl mx-auto p-8 mt-12">
            <div 
              key={currentStep} 
              className="animate-slide-left"
            >
              <CurrentStepComponent 
                onNext={handleNext}
                onPrevious={handlePrevious}
                isFirstStep={currentStep === 'markets'}
                isLastStep={currentStep === 'forecast'}
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default OnboardingLayout; 